import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BadgeCheck, Cake, CircleUserRound, Dna, Phone, User, Weight, type LucideIcon } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { TruncatedTextTooltip } from "@/components/ui/truncated-text-tooltip";
import { WhatsAppPhoneLink } from "@/components/ui/whatsapp-phone-link";
import { CustomIcons } from "@/components/icons/CustomIcons";
import { formatDateBR } from "@/lib/date-utils";
import { isPetDeceased } from "@/lib/utils/pet-death";
import { buildMedicalRecordPatientViewModel } from "@/components/medical-records/mobile/medical-record-patient-view-model";

type PatientCondition = {
  id: string;
  condition?: { name?: string | null } | null;
  name?: string | null;
};

type PatientSummaryPet = {
  id: string;
  name: string;
  photo?: { file_path?: string | null } | { file_path?: string | null }[] | null;
  gender?: { name?: string | null } | null;
  breed?: { name?: string | null } | null;
  status?: { name?: string | null } | null;
  sterilization?: { name?: string | null } | null;
  owner?: {
    id?: string | null;
    name?: string | null;
    phones?: Array<{
      phone_number?: string | null;
      is_primary?: boolean | null;
      is_whatsapp?: boolean | null;
    }> | null;
  } | null;
  allergies?: PatientCondition[] | null;
  temperaments?: PatientCondition[] | null;
  notes?: string | null;
};

type MedicalRecordPatientSummaryCardProps = {
  pet: PatientSummaryPet;
  age?: string | null;
  lastWeight?: { weight?: number | string | null; measured_at?: string | null } | null;
};

export const MedicalRecordPatientSummaryCard = ({
  pet,
  age,
  lastWeight,
}: MedicalRecordPatientSummaryCardProps) => {
  const { t, i18n } = useTranslation("medicalRecords");
  const patientViewModel = buildMedicalRecordPatientViewModel({
    pet,
    age,
    lastWeight,
    locale: i18n.resolvedLanguage ?? i18n.language,
  });
  const petPhoto = patientViewModel.photoUrl;
  const ownerPrimaryPhone =
    pet.owner?.phones?.find((phone) => phone.phone_number === patientViewModel.phoneNumber) ?? null;
  const ownerPrimaryPhoneLabel = patientViewModel.phoneNumber;
  const sterilizationName = pet.sterilization?.name?.trim().toLowerCase() ?? "";
  const sterilizationLabel = sterilizationName
    ? sterilizationName.includes("não") || sterilizationName.includes("nao")
      ? "Não castrado"
      : "Castrado"
    : null;
  const lastWeightSummary = lastWeight?.weight != null
    ? `${lastWeight.weight} kg${lastWeight.measured_at ? ` (${formatDateBR(lastWeight.measured_at)})` : ""}`
    : null;
  const petSummaryItems: Array<{ key: string; Icon: LucideIcon; label: string }> = [];

  if (pet.gender?.name) petSummaryItems.push({ key: "gender", Icon: CircleUserRound, label: pet.gender.name });
  if (pet.breed?.name) petSummaryItems.push({ key: "breed", Icon: Dna, label: pet.breed.name });
  if (age) petSummaryItems.push({ key: "age", Icon: Cake, label: age });
  if (sterilizationLabel) petSummaryItems.push({ key: "sterilization", Icon: BadgeCheck, label: sterilizationLabel });
  if (lastWeightSummary) petSummaryItems.push({ key: "weight", Icon: Weight, label: lastWeightSummary });

  const petAllergies = pet.allergies ?? [];
  const petTemperaments = pet.temperaments ?? [];

  return (
    <Card>
      <CardContent className="space-y-3 p-4 text-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex min-w-0 items-center gap-3 lg:max-w-[46%]">
            <Avatar className="h-14 w-14 shrink-0">
              {petPhoto ? (
                <AvatarImage src={petPhoto} alt={pet.name} />
              ) : (
                <AvatarFallback className="bg-muted/50 text-muted-foreground">
                  <CustomIcons name="paws" className="h-5 w-5 text-current" />
                </AvatarFallback>
              )}
            </Avatar>
            <div className="min-w-0 space-y-2">
              <p className="text-xs font-semibold uppercase text-muted-foreground">
                {t("patientCard.title")}
              </p>
              <div className="flex min-w-0 items-center gap-2">
                <Link
                  to={`/patients/pets/${pet.id}`}
                  className="truncate text-xl font-semibold text-primary hover:underline"
                >
                  {pet.name}
                </Link>
                {isPetDeceased(pet) && pet.status?.name ? (
                  <Badge variant="outline" className="shrink-0 text-[10px]">
                    {pet.status.name}
                  </Badge>
                ) : null}
              </div>
              {petSummaryItems.length > 0 ? (
                <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                  {petSummaryItems.map(({ key, Icon, label }) => (
                    <span key={key} className="flex min-w-0 items-center gap-1.5">
                      <Icon className="h-4 w-4 shrink-0" />
                      <span className="truncate">{label}</span>
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
          </div>

          <div className="min-w-0 space-y-2 lg:mr-10">
            <div className="flex min-w-0 items-start gap-2">
              <User className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0">
                <p className="text-xs font-semibold text-muted-foreground">{t("patientCard.guardian")}</p>
                {pet.owner?.id && pet.owner?.name ? (
                  <Link
                    to={`/patients/owners/${pet.owner.id}`}
                    className="block break-words font-semibold leading-snug text-primary hover:underline"
                  >
                    {pet.owner.name}
                  </Link>
                ) : (
                  <p className="text-muted-foreground">{t("patientCard.noGuardian")}</p>
                )}
              </div>
            </div>
            <div className="flex gap-10">
              <div className="flex min-w-0 items-center gap-2">
                <Phone className="h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-muted-foreground">{t("patientCard.phone")}</p>
                  {ownerPrimaryPhoneLabel ? (
                    ownerPrimaryPhone?.is_whatsapp ? (
                      <WhatsAppPhoneLink
                        phoneNumber={ownerPrimaryPhoneLabel}
                        className="min-w-0"
                        textClassName="truncate text-sm text-foreground hover:text-primary"
                      />
                    ) : (
                      <p className="truncate text-sm text-foreground">{ownerPrimaryPhoneLabel}</p>
                    )
                  ) : (
                    <p className="truncate text-sm text-muted-foreground">{t("patientCard.noPhone")}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {(petAllergies.length > 0 || petTemperaments.length > 0 || pet.notes) ? (
          <div className="flex flex-col gap-2 border-t pt-3 md:flex-row md:items-start">
            {petAllergies.length > 0 ? (
              <div className="flex min-w-0 flex-1 items-start gap-2">
                <span className="shrink-0 text-xs font-semibold text-muted-foreground">{t("patientCard.allergies")}</span>
                <div className="flex min-w-0 flex-wrap gap-1">
                  {petAllergies.map((item) => {
                    const name = item.condition?.name ?? item.name ?? "";
                    if (!name) return null;
                    return (
                      <Badge key={item.id} variant="default" className="max-w-full truncate">
                        {name}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            ) : null}
            {petTemperaments.length > 0 ? (
              <div className="flex min-w-0 flex-1 items-start gap-2">
                <span className="shrink-0 text-xs font-semibold text-muted-foreground">{t("patientCard.temperament")}</span>
                <div className="flex min-w-0 flex-wrap gap-1">
                  {petTemperaments.map((item) => {
                    const name = item.condition?.name ?? item.name ?? "";
                    if (!name) return null;
                    return (
                      <Badge key={item.id} variant="secondary" className="max-w-full truncate">
                        {name}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            ) : null}
            {pet.notes ? (
              <div className="flex min-w-0 flex-[1.2] items-start gap-2">
                <span className="shrink-0 text-xs font-semibold text-muted-foreground">{t("patientCard.patientNotes")}</span>
                <TruncatedTextTooltip
                  text={pet.notes}
                  className="min-w-0 flex-1 truncate text-sm text-foreground"
                />
              </div>
            ) : null}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};
